import type { CSSProperties } from 'react';
import { accentIconStop } from '@featit/shared/brand/colour-accents';

/**
 * F-0579 ACC-4 (`docs/concept-colour-accents.md`): the one mechanism every accented icon uses. An
 * icon spreads these props; `index.css` colours it from its frame stop under Accents › Colour, keeps
 * it still at rest, and redraws its outline once on mouse-over. In Quiet the props draw nothing, so
 * the icon is exactly what its own classes say.
 *
 * `position` is the icon's place in its list: the stops walk teal to azure down the list.
 */
export function accentIconProps(position: number): {
  'data-accent-icon': number;
  style: CSSProperties;
  ref: (icon: SVGSVGElement | null) => void;
} {
  const stop = accentIconStop(position);
  return {
    'data-accent-icon': stop,
    style: { '--evx-accent-icon': `var(--evx-icon-stop-${stop})` } as CSSProperties,
    ref: measureOutlines,
  };
}

const OUTLINES = 'path, line, polyline, polygon, circle, ellipse, rect';

/**
 * Every outline of the icon measures 1 long (`pathLength`), so the redraw's dash runs from 1 to 0
 * and each stroke draws over the whole 0.7 s, however long it is — as the prototype draws them. It
 * changes nothing at rest, where no dash is set.
 *
 * I-1755: a trace reads left to right. An outline that ends left of where it starts is marked
 * `data-accent-trace="reverse"`, and `index.css` draws it from its end.
 */
function measureOutlines(icon: SVGSVGElement | null): void {
  icon?.querySelectorAll(OUTLINES).forEach((outline) => {
    outline.setAttribute('pathLength', '1');
    if (tracesRightToLeft(outline)) outline.setAttribute('data-accent-trace', 'reverse');
    else outline.removeAttribute('data-accent-trace');
  });
}

type PointType = { x: number; y: number };

/** How far left of its start an outline must end to count as drawn right to left: float noise. */
const LEFTWARD = 0.01;

/**
 * Whether an outline, drawn from its start, would run right to left: its last point lies left of its
 * first. Read from the outline's own attributes, so it needs no layout. A closed shape (circle,
 * ellipse, rect, polygon, a path ending in `z`) and a stroke whose ends share an x keep their own
 * direction.
 */
export function tracesRightToLeft(outline: Element): boolean {
  const ends = outlineEnds(outline);
  return ends !== null && ends.end.x < ends.start.x - LEFTWARD;
}

function outlineEnds(outline: Element): { start: PointType; end: PointType } | null {
  const number = (name: string) => Number(outline.getAttribute(name) ?? 0);
  switch (outline.tagName.toLowerCase()) {
    case 'line':
      return {
        start: { x: number('x1'), y: number('y1') },
        end: { x: number('x2'), y: number('y2') },
      };
    case 'polyline': {
      const values = numbersIn(outline.getAttribute('points') ?? '');
      if (values.length < 4) return null;
      const [x1, y1] = values;
      const [x2, y2] = values.slice(-2);
      return { start: { x: x1, y: y1 }, end: { x: x2, y: y2 } };
    }
    case 'path':
      return pathEnds(outline.getAttribute('d') ?? '');
    default:
      return null;
  }
}

const NUMBER = /[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?/gi;

function numbersIn(text: string): number[] {
  return [...text.matchAll(NUMBER)].map((match) => Number(match[0]));
}

/** How many numbers each path command takes, per segment. */
const ARITY: Record<string, number> = {
  m: 2,
  l: 2,
  h: 1,
  v: 1,
  c: 6,
  s: 4,
  q: 4,
  t: 2,
  a: 7,
  z: 0,
};

/**
 * A path's first and last points, walking its commands (SVG 1.1 path grammar), or null for a path
 * that ends closed (`z`) or does not parse. Only the end point of each segment matters here, so a
 * curve is read as its last pair.
 */
export function pathEnds(d: string): { start: PointType; end: PointType } | null {
  const tokens = pathTokens(d);
  if (tokens === null || tokens.length === 0) return null;
  let at = { x: 0, y: 0 };
  let subpath = at;
  let start: PointType | null = null;
  let closed = false;
  for (const { command, args } of tokens) {
    const lower = command.toLowerCase();
    const relative = command === lower;
    closed = lower === 'z';
    if (closed) {
      at = subpath;
      continue;
    }
    const arity = ARITY[lower];
    for (let index = 0; index < args.length; index += arity) {
      const segment = args.slice(index, index + arity);
      const base = relative ? at : { x: 0, y: 0 };
      if (lower === 'h') at = { x: base.x + segment[0], y: at.y };
      else if (lower === 'v') at = { x: at.x, y: base.y + segment[0] };
      else at = { x: base.x + segment[arity - 2], y: base.y + segment[arity - 1] };
      // A moveto's later pairs are linetos; only its first pair starts a subpath.
      if (lower === 'm' && index === 0) subpath = at;
      start ??= at;
    }
  }
  return start === null || closed ? null : { start, end: at };
}

/**
 * A path's commands and their numbers. An arc's two flags are single digits that may stand
 * unseparated (`a1 1 0 01-1 1`), so they are read one character each.
 */
function pathTokens(d: string): { command: string; args: number[] }[] | null {
  const tokens: { command: string; args: number[] }[] = [];
  const number = /[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?/iy;
  let index = 0;
  const skip = () => {
    while (index < d.length && /[\s,]/.test(d[index])) index += 1;
  };
  skip();
  while (index < d.length) {
    const command = d[index];
    const arity = ARITY[command.toLowerCase()];
    if (arity === undefined) return null;
    index += 1;
    const args: number[] = [];
    skip();
    while (index < d.length && !(d[index].toLowerCase() in ARITY)) {
      const slot = args.length % 7;
      if (command.toLowerCase() === 'a' && (slot === 3 || slot === 4)) {
        if (d[index] !== '0' && d[index] !== '1') return null;
        args.push(Number(d[index]));
        index += 1;
      } else {
        number.lastIndex = index;
        const match = number.exec(d);
        if (match === null) return null;
        args.push(Number(match[0]));
        index = number.lastIndex;
      }
      skip();
    }
    if (arity === 0 ? args.length > 0 : args.length === 0 || args.length % arity !== 0) return null;
    tokens.push({ command, args });
  }
  return tokens;
}
